import React from "react";
import OrderDTO from "@/dto/order/OrderDTO";
import OrderStatus from "@/enum/OrderStatus";
import ShopType from "@/enum/ShopType";
import { useModalStore } from "@/hooks/Modal/hooks";
import DouDianUpdateConsigneeModal from "@/scenes/OrderManagement/components/dialogs/UpdateConsigneeDialog/DouDianUpdateConsigneeModal";
import UpdateConsigneeModal from "@/scenes/OrderManagement/components/dialogs/UpdateConsigneeDialog/UpdateConsigneeModal";
import { useGlobalStore } from "@/store/hooks";
import { SupplierShopTypes } from "@/utils/ShopUtils";
import { showChannelShopDisabledUpdateAddressModal } from "@/utils/dialog";
import {
    encryptedManualOrderTags,
    isAnyOrderWithJdConsolidationTag,
    isAnyOrderWithJdPurchaseTag,
    isAnyOrderWithTaoTianReissueTag,
    isOrderWithPresentTag,
} from "@/utils/OrderTagUtils";
import { Modal } from "antd";
import { observer } from "mobx-react-lite";

export function hasEncryptedTag(order: Pick<OrderDTO, "tags">, shopType: ShopType) {
    if (ShopType.MANUAL === shopType && order.tags) {
        return order.tags.some((orderTag) => encryptedManualOrderTags.includes(orderTag));
    }

    return false;
}

/**
 * 是否显示修改收货人信息按钮
 */
export function showUpdateConsigneeButton(order: Pick<OrderDTO, "preShipped" | "status" | "tags">, shopType: ShopType) {
    const isHide = [
        ...SupplierShopTypes,
        ShopType.JD_PURCHASE,
        ShopType.PDD_FDS,
        ShopType.PDD_FULFILLMENT,
        ShopType.ALIBABA_SUPPLIER,
    ].includes(shopType);

    const isEncrypted = hasEncryptedTag(order, shopType);

    if (isHide || order.preShipped || isEncrypted) return false;

    // 只有待发货、已发货订单可以修改地址
    if (
        order.status === OrderStatus.PENDING_SHIPMENT ||
        order.status === OrderStatus.SHIPPED ||
        order.status === OrderStatus.SUCCESS
    ) {
        return true;
    }

    return false;
}

interface UpdateConsigneeButtonProps {
    order: OrderDTO;
    onUpdateSuccess;
    className?: string;
}

const UpdateConsigneeButton: React.FC<UpdateConsigneeButtonProps> = observer(function (props) {
    const { order, onUpdateSuccess, className } = props;
    const { shopId } = order;

    const globalStore = useGlobalStore();
    const shop = globalStore.shopMapping.get(shopId);
    const modalStore = useModalStore();

    if (!shop) return null;

    const show = showUpdateConsigneeButton(order, shop.type);

    if (!show) {
        return null;
    }

    return (
        <a
            role="button"
            tabIndex={-1}
            className={className}
            onClick={(e) => {
                e.preventDefault();

                if (
                    [ShopType.CHANNEL_SHOP, ShopType.CHANNEL_SHOP_DROPSHIP, ShopType.CHANNEL_SHOP_SUPPLIER].includes(
                        shop.type,
                    ) &&
                    order.status !== OrderStatus.PENDING_SHIPMENT
                ) {
                    showChannelShopDisabledUpdateAddressModal();

                    return;
                }

                if (isAnyOrderWithJdConsolidationTag(order)) {
                    Modal.error({ title: "京东集运订单不支持修改地址" });

                    return;
                }

                if (isAnyOrderWithJdPurchaseTag([order])) {
                    Modal.error({ title: "京东供销订单不支持修改地址" });

                    return;
                }

                if (shop.type === ShopType.TAOBAO && isOrderWithPresentTag(order)) {
                    Modal.error({ title: "淘宝送礼单暂不支持修改收货地址" });

                    return;
                }

                if (isAnyOrderWithTaoTianReissueTag([order])) {
                    Modal.error({ title: "补发单暂不支持修改收货地址" });

                    return;
                }

                if (shop.type === ShopType.DOUDIAN) {
                    const disabled = [OrderStatus.SHIPPED, OrderStatus.SUCCESS].includes(order.status);

                    if (disabled) {
                        Modal.error({
                            width: 800,
                            title: <span className="fb">抖店已发货订单地址修改指引</span>,
                            content: (
                                <div>
                                    <p>
                                        因抖音电子面单规则限制(
                                        <a
                                            target="_blank"
                                            href="https://op.jinritemai.com/docs/notice-docs/5/7640?from=list&docLabel="
                                        >
                                            公告
                                        </a>
                                        )，抖店已发货和已完成订单不支持商家直接修改地址，您可按以下方式处理：
                                    </p>
                                    <p className="mt10">
                                        1.如买家已用新地址通过抖店平台的
                                        <a
                                            href="https://school.jinritemai.com/doudian/web/article/aHTMTVTqmDZG"
                                            target="_blank"
                                        >
                                            补寄
                                        </a>
                                        /
                                        <a
                                            href="https://school.jinritemai.com/doudian/web/article/114313"
                                            target="_blank"
                                        >
                                            换货
                                        </a>
                                        申请了售后单时，可复制售后单信息创建手工订单打印。
                                        <a
                                            href="https://qw15p3s8hv.feishu.cn/wiki/TY5SwlRNEiOeiukZXAQcYjzPnme"
                                            target="_blank"
                                        >
                                            查看教程
                                        </a>
                                    </p>
                                    <p className="mt10">
                                        2.若新地址打印运单号无需回传抖店，可创建手工订单并录入新地址(需使用非抖音电子面单打印)
                                        。
                                        <a
                                            href="https://qw15p3s8hv.feishu.cn/wiki/wikcncB5Ji6FahvJOStyb60ay0g#xw2qKK"
                                            target="_blank"
                                        >
                                            查看教程
                                        </a>
                                    </p>
                                </div>
                            ),
                        });

                        return;
                    }
                }

                modalStore.openModal(
                    shop.type === ShopType.DOUDIAN ? DouDianUpdateConsigneeModal : UpdateConsigneeModal,
                    {
                        order,
                        onSuccess: onUpdateSuccess,
                    },
                );
            }}
            style={{ userSelect: "none" }}
        >
            修改
        </a>
    );
});

export default UpdateConsigneeButton;
